import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

import { UserTimesheet } from '@app/timesheet-user/models/user-timesheet.interface';
import { TimesheetRow } from '@app/timesheet-user/models/timesheet-row.interface';
import { TimesheetDOS } from './timesheet-dos.service';
import { TimesheetRepoService } from './timesheet-repo.service';
import { ChangedValueDetails } from '@app/timesheet-user/models/changed-values.interface';
import { CORRECT_MONTH_NUMBER, TimesheetStatus } from '@app/shared/constants/timesheet.constants';
import { ExportType } from '@app/shared/components/export-box/export-box.constants';
import { triggerFileDownload } from '@app/shared/functions';
import { EXPORT_PREFIX } from '@app/shared/constants/export-prefix';
import { formatHours } from '../utils/format-hours.util';

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  timesheetId: number;

  constructor(private _timesheetRepoService: TimesheetRepoService, private _timesheetDOS: TimesheetDOS) {}

  onSuccessfulTimesheetSaveAction(): void {
    const currentTimesheetStatus = this._timesheetDOS.getTimesheetStatusSnapshot();
    if (currentTimesheetStatus === TimesheetStatus.New) {
      this._timesheetDOS.updateTimesheetStatus(TimesheetStatus.Open);
    }
    this.updatePreviousSavedTimesheetData();
  }

  loadTimesheet(): Observable<UserTimesheet> {
    this._timesheetDOS.updateValidationErrors(null);
    const [year, month] = this.getYearAndMonthFromDate(this._timesheetDOS.getTimesheetDateSnapshot());

    return this._timesheetRepoService.loadTimesheet(year, month).pipe(
      tap((data: UserTimesheet) => {
        this.handleDataUpdate(data);
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err);
      })
    );
  }

  loadUserTimesheet(username: string): Observable<UserTimesheet> {
    const [year, month] = this.getYearAndMonthFromDate(this._timesheetDOS.getTimesheetDateSnapshot());

    return this._timesheetRepoService.loadUserTimesheet(username, year, month).pipe(
      tap((data: UserTimesheet) => {
        this.handleDataUpdate(data);
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err);
      })
    );
  }

  submitTimesheetStatus(): Observable<undefined> {
    const [year, month] = this.getYearAndMonthFromDate(this._timesheetDOS.getTimesheetDateSnapshot());

    return this._timesheetRepoService.submitTimesheetStatus(year, month).pipe(
      tap((timesheetStatus) => this._timesheetDOS.updateTimesheetStatus(timesheetStatus)),
      catchError((err: HttpErrorResponse) => {
        return throwError(err);
      })
    );
  }

  updateTimesheet(timesheetId: number, rowId: number, changedValues: TimesheetRow): Observable<TimesheetRow> {
    return this._timesheetRepoService.updateTimesheet(timesheetId, rowId, changedValues);
  }

  updateTimesheetChangeStatus(changedFieldDetails: ChangedValueDetails): void {
    const rowId = changedFieldDetails.changedRowIndex;
    const field = changedFieldDetails.changedField;
    const isDescription = field === 'description';

    const newValue = isDescription ? changedFieldDetails.changedValue || '' : changedFieldDetails.changedValue;
    const savedValue = this._timesheetDOS.previousSavedUserTimesheetData.rowListDTO[rowId][field];
    const prevSavedValue = isDescription ? savedValue || '' : savedValue;

    if (prevSavedValue !== newValue) {
      this._timesheetDOS.changedTimesheetFieldsArr.push(changedFieldDetails);
      this._timesheetDOS.updateUserTimesheetChangedStatus(true);
    } else {
      this._timesheetDOS.changedTimesheetFieldsArr = this._timesheetDOS.changedTimesheetFieldsArr.filter((el) => {
        return el.changedRowIndex !== rowId || el.changedField !== field;
      });
      if (!this._timesheetDOS.changedTimesheetFieldsArr.length) {
        this._timesheetDOS.updateUserTimesheetChangedStatus(false);
      }
    }
  }

  updatePreviousSavedTimesheetData(): void {
    this._timesheetDOS.previousSavedUserTimesheetData = JSON.parse(
      JSON.stringify(this._timesheetDOS.updatedUserTimesheetData)
    );
    this._timesheetDOS.updateUserTimesheetChangedStatus(false);
    this._timesheetDOS.changedTimesheetFieldsArr.length = 0;
  }

  updateTimesheetData(): Observable<UserTimesheet> {
    const formattedRowData = this._timesheetDOS.updatedUserTimesheetData.rowListDTO.map((row) => {
      return {
        ...row,
        creativeHours: formatHours(row.creativeHours),
        totalHours: formatHours(row.totalHours)
      };
    });

    return this._timesheetRepoService
      .updateTimesheetData({ ...this._timesheetDOS.updatedUserTimesheetData, rowListDTO: formattedRowData })
      .pipe(
        tap((data) => {
          this._timesheetDOS.updateTimesheetData(data);
          this.onSuccessfulTimesheetSaveAction();
        })
      );
  }

  recallTimesheet(): Observable<TimesheetStatus> {
    return this._timesheetRepoService.recallTimesheet(this.timesheetId);
  }

  reopenTimesheet(): Observable<undefined> {
    return this._timesheetRepoService.reopenTimesheet(this.timesheetId);
  }

  exportTimesheet(format: ExportType): Observable<HttpResponse<Blob>> {
    const username = this._timesheetDOS.getViewModeUserSnapshot();
    const fullName = this._timesheetDOS.getTimesheetDataSnapshot().fullName;
    const currentDate = this._timesheetDOS.getTimesheetDateSnapshot();
    const [year, month] = [currentDate.getFullYear(), currentDate.getMonth()];
    const monthName = currentDate.toLocaleDateString('en', { month: 'long' });
    const correctedMonth = month + 1;
    const filename = `${EXPORT_PREFIX}_${fullName.replace(' ', '_')}_${monthName}_${year}.${format}`;

    return this._timesheetRepoService.exportTimesheet(username, year, correctedMonth, filename, format).pipe(
      tap((response: HttpResponse<Blob>) => {
        triggerFileDownload(response.body, filename);
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err);
      })
    );
  }

  private handleDataUpdate(data: UserTimesheet): void {
    this.timesheetId = data.timesheetId;
    this._timesheetDOS.updateTimesheetStatus(data.timesheetStatus);
    this._timesheetDOS.updateTimesheetData(data);
  }

  private getYearAndMonthFromDate(date?: Date): [number, number] {
    const selectedDate: Date = date ? date : new Date();
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + CORRECT_MONTH_NUMBER;
    return [year, month];
  }
}
